import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";

const STATUS = {
  done: ["Done today", "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", CheckCircle2],
  due: ["Due today", "border-amber-500/40 bg-amber-500/10 text-amber-300", Clock],
  overdue: ["Overdue", "border-red-500/40 bg-red-500/10 text-red-300", AlertTriangle],
};

const Summary = ({ s }) => (
  <div className="mb-6 grid grid-cols-3 gap-3" data-testid="my-checks-summary">
    {Object.entries(STATUS).map(([k, [l, c, I]]) => (
      <div key={k} className={`rounded-md border p-4 ${c}`} data-testid={`my-checks-count-${k}`}>
        <I className="mb-2 h-5 w-5" /><p className="font-display text-3xl font-extrabold">{s[k]}</p><p className="eyebrow">{l}</p>
      </div>
    ))}
  </div>
);

const MachineRow = ({ m, onStart }) => {
  const [l, c] = STATUS[m.check_status];
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-800/70 px-4 py-3 last:border-0" data-testid={`my-check-${m.asset_code}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold"><span className="mr-2 font-mono text-xs text-cyan-300">{m.asset_code}</span>{m.name}</p>
        <p className="text-xs text-slate-400">Last check {m.last_check ? fmtDateTime(m.last_check) : "never"}{m.assigned_to?.length ? ` - ${m.assigned_to.join(", ")}` : ""}</p>
      </div>
      <span className={`rounded border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${c}`} data-testid={`my-check-status-${m.asset_code}`}>{l}</span>
      <Button size="sm" onClick={onStart} className={m.check_status === "done" ? "border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800" : "bg-rose-600 hover:bg-rose-700"} variant={m.check_status === "done" ? "outline" : "default"} data-testid={`my-check-start-${m.asset_code}`}>
        <ClipboardCheck className="mr-1 h-4 w-4" />{m.check_status === "done" ? "Check again" : "Start check"}
      </Button>
    </div>
  );
};

export default function MyChecks() {
  const { can } = useAuth();
  const { site } = useSite();
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/my-checks", { params: { all } }).then((r) => setData(r.data)), [all]);
  useEffect(() => { if (site) load(); }, [load, site]);
  const isMgr = can("puwer", "manage");
  return (
    <div data-testid="my-checks-page">
      <PageHeader eyebrow={site?.name} title={all ? "All machine checks" : "My machine checks"} description="Daily machine safety checks for the sections and machines assigned to you. Missed checks alert your Supervisor at 4pm and escalate each day they stay missed.">
        {isMgr && (
          <div className="flex rounded-md border border-slate-700 p-1" data-testid="my-checks-scope">
            {[[false, "Mine"], [true, "All machines"]].map(([v, l]) => (
              <button key={l} onClick={() => setAll(v)} className={`h-9 rounded px-3 text-sm transition-colors ${all === v ? "bg-rose-600 text-white" : "text-slate-400"}`} data-testid={`my-checks-scope-${v ? "all" : "mine"}`}>{l}</button>
            ))}
          </div>
        )}
      </PageHeader>
      {data && <Summary s={data.summary} />}
      {data && !data.sections.length && (
        <div className="panel p-8 text-center text-sm text-slate-400" data-testid="my-checks-empty">
          No machines are assigned to you on this site yet. Ask your manager to assign your section in Users & Roles.
        </div>
      )}
      <div className="space-y-5">
        {data?.sections.map((s) => (
          <div key={s.section} className="panel" data-testid={`my-checks-section-${s.section}`}>
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <p className="font-display text-lg font-bold uppercase">{s.section}</p>
              <p className="text-xs text-slate-400">{s.machines.filter((m) => m.check_status === "done").length} / {s.machines.length} done</p>
            </div>
            {s.machines.map((m) => <MachineRow key={m.id} m={m} onStart={() => nav(`/inspect/machine_daily?asset=${m.id}`)} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
